import { useEffect, useState } from 'react'

import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material'
import { Building2, User, LogOut } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { ROUTES } from '../../constants'
import { clearBranchScopedCache } from '../../hooks/queryClient'
import {
  getUserDetails,
  isBranchInactiveForUser,
  mergeUserStatusFromDetails,
  setSelectedBranch,
} from '../../utils'
import { fetchAndCacheBranchPermissions } from '../../utils/permissions'
import { logout as clearSession } from '../auth/login/auth'

function BranchIcon() {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#3b82f6',
        color: '#fff',
        flexShrink: 0,
      }}
    >
      <Building2 size={20} />
    </Box>
  )
}

function BranchCard({ branch, onSelect }) {
  const status = String(branch.status || '').toUpperCase()
  const isInactive = status && status !== 'ACTIVE'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: '1px solid var(--divider-color)',
        bgcolor: 'var(--card-bg)',
        transition: 'background 0.15s ease, border-color 0.15s ease',
        opacity: isInactive ? 0.7 : 1,
        '&:hover': {
          borderColor: 'var(--sidebar-border)',
          bgcolor: 'var(--sidebar-hover-bg)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <BranchIcon />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color="var(--text-primary)"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {branch.name}
          </Typography>
          {isInactive && (
            <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
              {status} — Contact administrator to access
            </Typography>
          )}
        </Box>
      </Box>
      <Button
        variant="contained"
        onClick={() => onSelect(branch)}
        sx={{
          bgcolor: isInactive ? '#ef4444' : 'var(--text-primary)',
          color: 'var(--body-bg)',
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          boxShadow: 'none',
          '&:hover': { opacity: 0.9, boxShadow: 'none' },
        }}
      >
        Select
      </Button>
    </Box>
  )
}

function BranchSelection() {
  const navigate = useNavigate()
  const location = useLocation()
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null)
  const branchInactiveRedirect = Boolean(location.state?.branchInactive)

  // Build the branch list shown to the user. The `/me` payload carries the
  // user's status in each branch via `Status[]` keyed by `branchId`, so we
  // merge that user-level status into the branch object here. The branch's
  // own `status` (e.g. ACTIVE for the branch itself) is used as a fallback
  // when no matching per-user entry exists.
  const buildSnapshot = () => {
    const details = getUserDetails() || {}
    const branches = mergeUserStatusFromDetails(details).map((b) => ({
      id: b.id ?? b.uuid,
      uuid: b.uuid,
      name: b.name,
      status: b.status || '',
    }))
    return { user: details, branches }
  }

  // Re-read on every focus so the latest `/me` payload is reflected even if
  // the user navigated back to this page in the same session.
  const [snapshot, setSnapshot] = useState(buildSnapshot)

  useEffect(() => {
    const refresh = () => setSnapshot(buildSnapshot())
    window.addEventListener('focus', refresh)
    window.addEventListener('storage', refresh)
    refresh()
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  const { user, branches } = snapshot

  const handleSelect = async (branch) => {
    // Block navigation when the picked branch is not active for this user.
    // The `/me` payload exposes the user's status per branch; an inactive
    // branch should not be carried forward, so we surface an error and bail.
    if (isBranchInactiveForUser(branch)) {
      const status = String(branch.status || '').toUpperCase()
      await Swal.fire({
        title: 'Access Denied',
        text: `You are not allowed to access "${branch.name}" because your account is ${status.toLowerCase()} in this branch. Please contact your administrator.`,
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
        background: 'var(--card-bg, #ffffff)',
        color: 'var(--text-primary, #212529)',
      })
      return
    }

    // Fetch the branch-scoped permission payload before touching the
    // stored branch. The request carries the *picked* branch's `branch-id`
    // header, and a failure aborts the switch so the app never runs with a
    // branch whose permissions were never loaded.
    const permissionResult = await fetchAndCacheBranchPermissions(branch)
    if (!permissionResult.ok) {
      await Swal.fire({
        title: 'Unable to load permissions',
        text: `Permissions for "${branch.name}" could not be loaded. Please check your connection and try again.`,
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
        background: 'var(--card-bg, #ffffff)',
        color: 'var(--text-primary, #212529)',
      })
      return
    }

    // Persist the picked branch so it survives reloads / re-logins. This
    // MUST happen before `clearBranchScopedCache()` so the page reload that
    // helper triggers boots with the freshly picked branch instead of
    // resurrecting the previous one from localStorage.
    setSelectedBranch(branch)
    // Navigate to /dashboard BEFORE invoking clearBranchScopedCache().
    // clearBranchScopedCache() synchronously calls window.location.reload(),
    // so the URL must already point at /dashboard by then — otherwise the
    // reload lands back here on /branch-selection and the user is stuck
    // in a reload loop on the selection screen.
    navigate(ROUTES.DASHBOARD, { replace: true })
    // Clear React Query cache so dashboard + subsequent pages refetch with
    // the new `branch-id` header instead of showing the previous branch.
    // (clearBranchScopedCache() has its own internal try/catch and reloads
    // the page, so no wrapper is needed here.)
    clearBranchScopedCache()
  }

  const openProfileMenu = (event) => setProfileMenuAnchor(event.currentTarget)
  const closeProfileMenu = () => setProfileMenuAnchor(null)

  const handleLogout = () => {
    closeProfileMenu()
    clearSession()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--body-bg)',
        color: 'var(--text-primary)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* User identity strip */}
      <Box
        sx={{
          height: 64,
          bgcolor: 'var(--header-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          borderBottom: '1px solid var(--divider-color)',
        }}
      >
        <Typography variant="h6" fontWeight={600} color="var(--text-primary)">
          Welcome {user.name?.trim() || 'User'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            onClick={openProfileMenu}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              border: '1px solid var(--divider-color)',
              bgcolor: 'var(--card-bg)',
              cursor: 'pointer',
              '&:hover': { borderColor: 'var(--sidebar-border)' },
            }}
          >
            <IconButton
              size="small"
              sx={{
                color: 'var(--text-primary)',
                border: '1px solid var(--divider-color)',
                borderRadius: 999,
                p: 0.4,
              }}
            >
              <User size={14} />
            </IconButton>
            <Box sx={{ textAlign: 'left', lineHeight: 1.1 }}>
              <Typography variant="body2" fontWeight={600} color="var(--text-primary)">
                {user.name?.trim() || 'User'}
              </Typography>
              <Typography variant="caption" color="var(--text-secondary)">
                {user.isRoot ? 'Root User' : user.type || 'User'}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={closeProfileMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 200, backgroundColor: 'var(--modal-bg-color)' },
            }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography variant="body2" fontWeight={600} color="var(--text-primary)">
                {user.name?.trim() || 'User'}
              </Typography>
              <Typography variant="caption" color="var(--text-secondary)">
                {user.email || (user.isRoot ? 'Root User' : user.type || 'User')}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogOut size={16} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Branches list */}
      <Box
        sx={{
          flex: 1,
          maxWidth: 920,
          width: '100%',
          mx: 'auto',
          py: 6,
          px: 3,
        }}
      >
        <Typography variant="h5" fontWeight={700} color="var(--text-primary)" sx={{ mb: 3 }}>
          Branch Selection
        </Typography>

        {branchInactiveRedirect && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={false}>
            Your previously selected branch is no longer active for your account. Please pick
            another branch to continue.
          </Alert>
        )}

        {branches.length === 0 && (
          <Typography variant="body2" color="var(--text-secondary)">
            No branches available for this account.
          </Typography>
        )}

        {branches.map((b) => (
          <BranchCard key={b.uuid || b.id} branch={b} onSelect={handleSelect} />
        ))}
      </Box>
    </Box>
  )
}

export default BranchSelection
